// File: PolarChart.js
import React, { useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist-min';
import PropTypes from 'prop-types';

export default function PolarChart({ scores, axes }) {
  const chartRef = useRef(null);

  // Manually insert <br> every ~30 characters
  const wrapText = (text, limit = 30) => {
    if (!text) return '';
    const words = text.split(' ');
    const lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= limit) {
        currentLine += word + ' ';
      } else {
        lines.push(currentLine.trim());
        currentLine = word + ' ';
      }
    });

    if (currentLine) lines.push(currentLine.trim());

    return lines.join('<br>');
  };

  useEffect(() => {
    if (!scores || !axes || !chartRef.current) return;

    const labels = axes.map(axis => axis.label);
    const values = axes.map(axis => {
      const score = parseFloat(scores[axis.key]?.score);
      return isNaN(score) ? 0 : score;
    });

    const text = axes.map(axis => {
      const rawScore = parseFloat(scores[axis.key]?.score);
      const rounded = isNaN(rawScore)
        ? 'N/A'
        : Number.isInteger(rawScore)
        ? rawScore.toFixed(0)
        : rawScore.toFixed(1);

      const explanation = wrapText(scores[axis.key]?.explanation || '', 30);
      return `${axis.label}<br><b>Score:</b> ${rounded}${explanation ? `<br>${explanation}` : ''}`;
    });

    const data = [
      {
        type: 'scatterpolar',
        r: values,
        theta: labels,
        fill: 'toself',
        text: text,
        hoverinfo: 'text',
        line: { color: '#3b82f6' },
        fillcolor: 'rgba(59, 130, 246, 0.3)',
        marker: { color: '#3b82f6' },
      },
    ];

    const layout = {
      margin: { t: 50, b: 50, l: 20, r: 20 },
      polar: {
        radialaxis: {
          visible: true,
          range: [0, 10],
          tickfont: { size: 10 },
        },
      },
      hoverlabel: {
        bgcolor: '#ffffff',
        bordercolor: '#cbd5e1',
        font: {
          color: '#0f172a',
          size: 11,
          family: 'Inter, sans-serif',
        },
        align: 'left',
      },
      paper_bgcolor: '#f9fafb',
      plot_bgcolor: '#f9fafb',
    };

    Plotly.newPlot(chartRef.current, data, layout, { responsive: true });

    return () => {
      if (chartRef.current) {
        Plotly.purge(chartRef.current);
      }
    };
  }, [scores, axes]);

  return <div ref={chartRef} className="w-full h-[400px]" />;
}

PolarChart.propTypes = {
  scores: PropTypes.object.isRequired,
  axes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};
